/**
 * EP P2P — aligned with `packages/backend/src/modules/ep/ep.routes.ts`
 */
import { api } from './client';
import { mapEpOrderList, type EpOrderApiRow } from './ep-mappers';
export type { EpOrderUi } from './ep-mappers';
import type { EpOrderUi } from './ep-mappers';
import type { ListResponse, PaginationParams } from './types';

export type EpOrderListParams = PaginationParams & {
  status?: string;
  seller_id?: number;
  buyer_id?: number;
  /** Legacy PHP mirror: ep1 | ep2 */
  ep_type?: 'ep1' | 'ep2';
  min_amount?: string;
  max_amount?: string;
};

export type CreateEpOrderRequest = {
  ep_type?: 'ep1' | 'ep2';
  amount_ep: string;
  price_cny: string;
  payment_methods: Array<'bank' | 'alipay' | 'wechat' | 'usdt'>;
  note?: string;
};

export const epAPI = {
  getGuide: () =>
    api.get<{
      title_zh: string;
      hall_open: boolean;
      asset_note: string;
      status_zh: Record<string, string>;
      lifecycle: string[];
      screens: Array<{ screen_zh: string; method: string; path: string }>;
    }>('/api/ep/guide'),

  listOrders: async (params?: EpOrderListParams): Promise<ListResponse<EpOrderUi>> => {
    const raw = await api.get<ListResponse<EpOrderApiRow>>('/api/ep/orders', params);
    return mapEpOrderList(raw);
  },

  getOrderbook: (params?: { depth?: number }) =>
    api.get<{
      bids: Array<{ price: string; amount: string; total: string }>;
      asks: Array<{ price: string; amount: string; total: string }>;
      last_price?: string;
      change_pct?: string;
    }>('/api/ep/orderbook', params),

  getDailyQuota: () => api.get<{ used: boolean }>('/api/ep/daily-quota'),

  create: (request: CreateEpOrderRequest) =>
    api.post<EpOrderApiRow>('/api/ep/orders', request),

  claim: (request: { order_id: string }) =>
    api.post<EpOrderApiRow>('/api/ep/orders/claim', request),

  releaseClaim: (request: { order_id: string }) =>
    api.post<EpOrderApiRow>('/api/ep/orders/release-claim', request),

  markPaid: (request: { order_id: string; pay_proof?: string }) =>
    api.post<EpOrderApiRow>('/api/ep/orders/paid', request),

  release: (request: { order_id: string }) =>
    api.post<EpOrderApiRow>('/api/ep/orders/release', request),

  cancel: (request: { order_id: string }) =>
    api.post<EpOrderApiRow>('/api/ep/orders/cancel', request),

  getUsdtPayConfig: (order_id: string) =>
    api.get<EpUsdtPayConfig>('/api/ep/orders/usdt-pay-config', { order_id }),

  prepareUsdtPay: (request: { order_id: string }) =>
    api.post<EpUsdtPayConfig>('/api/ep/orders/usdt-pay', request),

  /** Staging MockUSDT — settle without WalletConnect. */
  mockUsdtPay: (request: { order_id: string }) =>
    api.post<EpOrderApiRow>('/api/ep/orders/usdt-mock-pay', request),
};

export type EpUsdtPayConfig = {
  order_id: string;
  chain_id: number;
  usdt_contract: string;
  usdt_decimals: number;
  seller_id: number;
  seller_wallet: string;
  buyer_wallet: string | null;
  payment_usdt: string;
  amount_ep: string;
  price_cny: string;
  cny_per_usdt: string;
  status: string;
  mock_pay_allowed?: boolean;
};
